import { AddressBookItem } from "../addressBook";
import { TokenPriceDataByTokenSymbol, TradePathBySupportedTokens, TradePathData } from "../api";
import { ApiPermissions, MarketNetwork } from "../cefi";
import { Env } from "../env";
import { Partner } from "../partners";
import { GroupedBalances } from "../portfolio";
import { UserSettings } from "../settings";
import { TokenSymbolName, OHLCV } from "../shared";
import { AccountId, VaultItem } from "../vault";
import {
  AddCexParams,
  BalancesParams,
  CancelOrderParams,
  CancelOrderStatus,
  ConnectParams,
  CreateOrderParams,
  DepositAddress,
  DepositParams,
  DepositableToken,
  DepositableTokensParams,
  EditOrderParams,
  ExchangeInfo,
  GetMinAmountsParams,
  GetNetworksParams,
  GetOHLCVParams,
  GetSupportedTokensParams,
  GetTradePathBySupportedTokensParams,
  GetTradePathParams,
  GetWhitelistedAddressesParams,
  GetWidgetBundleUrlParams,
  GetWithdrawalByIdParams,
  GroupedBalancesByAccount,
  MarketPair,
  MarketPairsParams,
  MarketRate,
  MarketRateParams,
  MinAmounts,
  Nft,
  NftsParams,
  OpenOrdersParams,
  OpenTabParams,
  Order,
  PrepareWithdrawalParams,
  PreparedOrder,
  ProviderConfig,
  Result,
  RetrieveOrderParams,
  SearchTransactionParams,
  TokenSymbol,
  TokenWithMarketNetworkAndTokenName,
  TotupPath,
  TotupWalletTypeParams,
  Transaction,
  TransactionsParams,
  VaultAccountsParams,
  VaultId,
  VerifyOrderParams,
  VerifyKYCParams,
  VerifyKYCReturn,
  WhitelistedAddresses,
  WithdrawToDefiParams,
  WithdrawableTokensParams,
} from "./types";

// Origin and selected vault ids are filled afterward
export type ConnectMethod = (params?: ConnectParams) => Promise<VaultItem[]>;

export type DisconnectMethod = () => Promise<Result>;

export type VaultsMethod = () => Promise<VaultItem[]>;

export type VaultAccountsMethod = (params: VaultAccountsParams) => Promise<VaultItem>;

export type ActiveVaultMethod = () => Promise<VaultId>;

export type SupportedExchangesMethod = () => Promise<ExchangeInfo[]>;

export type GetWithdrawalByIdMethod = (params: GetWithdrawalByIdParams) => Promise<Transaction>;

export type TransactionsMethod = (params: TransactionsParams) => Promise<Transaction[]>;

export type BalancesMethod = (params: BalancesParams) => Promise<GroupedBalancesByAccount>;

export type OpenOrdersMethod = (params: OpenOrdersParams) => Promise<Order[]>;

export type CreateOrderMethod = (params: CreateOrderParams) => Promise<Order>;

export type PrepareOrderMethod = (params: CreateOrderParams) => Promise<PreparedOrder>;

export type NftsMethod = (params: NftsParams) => Promise<{ nfts: Nft[]; total: number }>;

export type RetrieveOrderMethod = (params: RetrieveOrderParams) => Promise<Order>;

export type EditOrderMethod = (params: EditOrderParams) => Promise<Order>;

export type GetMinAmountsMethod = (params: GetMinAmountsParams) => Promise<MinAmounts>;

export type DepositableTokensMethod = (params: DepositableTokensParams) => Promise<DepositableToken[]>;

export type IsWalletTypeTotupNeededMethod = (params: TotupWalletTypeParams) => Promise<TotupPath[]>;

export type GetNetworksMethod = (params: GetNetworksParams) => Promise<MarketNetwork[]>;

export type DepositMethod = (params: DepositParams) => Promise<DepositAddress>;

export type MarketPairsMethod = (params: MarketPairsParams) => Promise<MarketPair[]>;

export type GetMarketRateMethod = (params: MarketRateParams) => Promise<MarketRate>;

export type CancelOrderMethod = (params: CancelOrderParams) => Promise<CancelOrderStatus>;

export type AddCexMethod = (params: AddCexParams) => Promise<Result>;

export type WithdrawToDefiMethod = (params: WithdrawToDefiParams) => Promise<Transaction>;

export type GetWhitelistedAddressesMethod = (params: GetWhitelistedAddressesParams) => Promise<WhitelistedAddresses[]>;

export type PrepareWithdrawalMethod = (params: PrepareWithdrawalParams) => Promise<Result>;

export type VerifyOrderMethod = (params: VerifyOrderParams) => Promise<void>;

export type WithdrawableTokensMethod = (params: WithdrawableTokensParams) => Promise<GroupedBalances>;

export type GetPricesMethod = () => Promise<TokenPriceDataByTokenSymbol>;

export type GetSettingsMethod = () => Promise<UserSettings>;

export type GetEnvironmentMethod = () => Promise<Env>;

export type GetAddressBookMethod = () => Promise<AddressBookItem[]>;

export type CheckAddressIsWhitelistedMethod = (params: {
  accountId: AccountId;
  key: string;
  address: string;
  tokenSymbol: TokenSymbol;
}) => Promise<boolean>;

export type SearchTransactionMethod = (params: SearchTransactionParams) => Promise<Transaction | null>;

export type GetWidgetBundleUrlMethod = (params: GetWidgetBundleUrlParams) => string;

export type GetTradePathMethod = (params: GetTradePathParams) => Promise<TradePathData[]>;

export type GetTradePathBySupportedTokensMethod = (
  params: GetTradePathBySupportedTokensParams,
) => Promise<TradePathBySupportedTokens>;

export type GetSupportedTokensMethod = (
  params: GetSupportedTokensParams,
) => Promise<TokenWithMarketNetworkAndTokenName[]>;

export type TrackLinkMethod = (params: { link: string; exchangeId: string }) => Promise<number | undefined>;

export type OpenPageWithHelperMethod = (params: {
  exchangeId: string;
  permissions: ApiPermissions[];
  link: string;
}) => Promise<string | undefined>;

export type UntrackLinkMethod = (params: { tabId: number }) => Promise<string>;

export type GetConfigMethod = () => Promise<ProviderConfig>;

export type FetchPartnersMethod = () => Promise<Partner[]>;

export type GetCurrentAppTab = () => Promise<number | undefined>;

export type FocusTabMethod = (params: { id: number }) => Promise<string>;

export type GetUserIdMethod = () => Promise<string>;

export type VerifyKYCMethod = (params: VerifyKYCParams) => Promise<VerifyKYCReturn>;

export type GetTokenNamesAndSymbolsMethod = () => Promise<TokenSymbolName[]>;

export type GetOHLCVMethod = (params: GetOHLCVParams) => Promise<OHLCV[]>;

export type OpenTabMethod = (params: OpenTabParams) => Promise<number | undefined>;

export type CanByPassApprovalTradeHubMethod = () => Promise<boolean>;

export type SetByPassApprovalTradeHubMethod = (params: { bypassApprovalTradeHub: boolean }) => Promise<boolean>;

export type GetExchangeUserIdMethod = (params: { accountId: string }) => Promise<{ userId: string }>;
